<template>
  <div class="notifications-demo">
    <!-- 🎯 Header -->
    <v-card class="mb-6" elevation="2">
      <v-card-title class="pa-6 bg-gradient-to-r from-orange-500 to-red-600 text-white">
        <div class="d-flex align-center">
          <v-icon icon="mdi-bell" size="32" class="me-3" />
          <div>
            <h2 class="text-h4 mb-0">🔔 Notifications Component Demo</h2>
            <p class="text-subtitle-1 mb-0 opacity-90">Sistema de notificaciones en tiempo real</p>
          </div>
        </div>
      </v-card-title>
    </v-card>

    <!-- 🔧 Demo Controls -->
    <v-card class="mb-4" elevation="1">
      <v-card-text class="pa-4">
        <v-row align="center">
          <v-col cols="12" md="3">
            <v-select
              v-model="notificationType"
              :items="notificationTypes"
              item-title="label"
              item-value="value"
              label="Tipo de Notificación"
              variant="outlined"
              density="compact"
            />
          </v-col>
          <v-col cols="12" md="3">
            <v-select
              v-model="notificationPosition"
              :items="positionOptions"
              item-title="label"
              item-value="value"
              label="Posición"
              variant="outlined"
              density="compact"
            />
          </v-col>
          <v-col cols="12" md="3">
            <v-switch
              v-model="autoHide"
              label="Auto ocultar"
              color="primary"
              hide-details
            />
          </v-col>
          <v-col cols="12" md="3">
            <v-btn
              color="primary"
              variant="flat"
              prepend-icon="mdi-plus"
              @click="createTestNotification"
              block
            >
              Crear Notificación
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- 🔔 Notifications Container -->
    <v-card elevation="1" class="mb-6">
      <v-card-title class="pa-4 bg-surface-variant">
        <v-icon icon="mdi-bell-outline" class="me-2" />
        Centro de Notificaciones
        <v-spacer />
        <v-chip
          :color="unreadCount > 0 ? 'error' : 'success'"
          size="small"
          variant="flat"
        >
          {{ unreadCount }} sin leer
        </v-chip>
      </v-card-title>

      <v-card-text class="pa-0">
        <div class="notifications-list">
          <div
            v-for="notification in displayedNotifications"
            :key="notification.id"
            :class="[
              'notification-item',
              `notification-${notification.type}`,
              { 'notification-unread': !notification.read }
            ]"
            @click="markAsRead(notification)"
          >
            <div class="d-flex align-start">
              <v-avatar
                :color="getNotificationColor(notification.type)"
                size="40"
                class="me-3"
              >
                <v-icon
                  :icon="getNotificationIcon(notification.type)"
                  color="white"
                  size="20"
                />
              </v-avatar>

              <div class="flex-grow-1">
                <div class="d-flex align-center justify-space-between mb-1">
                  <h4 class="text-subtitle-1 mb-0">{{ notification.title }}</h4>
                  <div class="d-flex align-center">
                    <span class="text-caption text-medium-emphasis me-2">
                      {{ formatTime(notification.timestamp) }}
                    </span>
                    <v-btn
                      icon="mdi-close"
                      variant="text"
                      size="small"
                      @click.stop="dismissNotification(notification)"
                    />
                  </div>
                </div>
                
                <p class="text-body-2 mb-2">{{ notification.message }}</p>
                
                <div class="d-flex align-center justify-space-between">
                  <v-chip
                    :color="getNotificationColor(notification.type)"
                    size="small"
                    variant="outlined"
                  >
                    {{ notification.category }}
                  </v-chip>
                  
                  <div v-if="notification.actions" class="d-flex gap-2">
                    <v-btn
                      v-for="action in notification.actions"
                      :key="action.label"
                      :color="action.color || 'primary'"
                      variant="outlined"
                      size="small"
                      @click.stop="executeAction(notification, action)"
                    >
                      {{ action.label }}
                    </v-btn>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div v-if="displayedNotifications.length === 0" class="text-center pa-8">
            <v-icon icon="mdi-bell-off" size="48" color="grey" class="mb-2" />
            <p class="text-subtitle-1">No hay notificaciones</p>
            <p class="text-caption text-medium-emphasis">Las nuevas notificaciones aparecerán aquí</p>
          </div>
        </div>
      </v-card-text>

      <v-card-actions class="pa-4" v-if="notifications.length > 0">
        <v-btn
          variant="outlined"
          prepend-icon="mdi-check-all"
          @click="markAllAsRead"
        >
          Marcar todas como leídas
        </v-btn>
        <v-spacer />
        <v-btn
          variant="outlined"
          color="error"
          prepend-icon="mdi-delete-sweep"
          @click="clearAllNotifications"
        >
          Limpiar todas
        </v-btn>
      </v-card-actions>
    </v-card>

    <!-- 📊 Notification Settings -->
    <v-row>
      <v-col cols="12" md="6">
        <v-card elevation="1">
          <v-card-title class="pa-4 bg-surface-variant">
            <v-icon icon="mdi-cog" class="me-2" />
            Configuración de Notificaciones
          </v-card-title>
          <v-card-text class="pa-4">
            <v-list>
              <v-list-item>
                <v-list-item-title>Notificaciones de incidencias</v-list-item-title>
                <template #append>
                  <v-switch
                    v-model="settings.incidents"
                    color="primary"
                    hide-details
                  />
                </template>
              </v-list-item>
              
              <v-list-item>
                <v-list-item-title>Alertas del sistema</v-list-item-title>
                <template #append>
                  <v-switch
                    v-model="settings.system"
                    color="primary"
                    hide-details
                  />
                </template>
              </v-list-item>
              
              <v-list-item>
                <v-list-item-title>Notificaciones de tareas</v-list-item-title>
                <template #append>
                  <v-switch
                    v-model="settings.tasks"
                    color="primary"
                    hide-details
                  />
                </template>
              </v-list-item>
              
              <v-list-item>
                <v-list-item-title>Sonido de notificaciones</v-list-item-title>
                <template #append>
                  <v-switch
                    v-model="settings.sound"
                    color="primary"
                    hide-details
                  />
                </template>
              </v-list-item>
            </v-list>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" md="6">
        <v-card elevation="1">
          <v-card-title class="pa-4 bg-surface-variant">
            <v-icon icon="mdi-chart-bar" class="me-2" />
            Estadísticas de Notificaciones
          </v-card-title>
          <v-card-text class="pa-4">
            <div
              v-for="stat in notificationStats"
              :key="stat.type"
              class="d-flex align-center justify-space-between mb-3"
            >
              <div class="d-flex align-center">
                <v-icon
                  :icon="getNotificationIcon(stat.type)"
                  :color="getNotificationColor(stat.type)"
                  class="me-2"
                />
                <span>{{ stat.label }}</span>
              </div>
              <div class="text-end">
                <div class="text-h6">{{ stat.count }}</div>
                <div class="text-caption text-medium-emphasis">últimos 7 días</div>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- 🔄 Back Navigation -->
    <v-card class="mt-6" elevation="1">
      <v-card-actions class="pa-4">
        <v-btn
          variant="outlined"
          prepend-icon="mdi-arrow-left"
          @click="$router.push('/dev-center/components')"
        >
          Volver a Componentes
        </v-btn>
        <v-spacer />
        <v-btn
          color="primary"
          variant="flat"
          prepend-icon="mdi-code-tags"
          @click="showCode"
        >
          Ver Código
        </v-btn>
      </v-card-actions>
    </v-card>

    <!-- Toast Notifications Overlay -->
    <div :class="['toast-container', `toast-${notificationPosition}`]">
      <transition-group name="toast" tag="div">
        <div
          v-for="toast in toastNotifications"
          :key="toast.id"
          :class="['toast-notification', `toast-${toast.type}`]"
          @click="dismissToast(toast)"
        >
          <div class="d-flex align-center">
            <v-icon
              :icon="getNotificationIcon(toast.type)"
              class="me-2"
              color="white"
            />
            <div class="flex-grow-1">
              <div class="toast-title">{{ toast.title }}</div>
              <div class="toast-message">{{ toast.message }}</div>
            </div>
            <v-btn
              icon="mdi-close"
              variant="text"
              size="small"
              color="white"
              @click.stop="dismissToast(toast)"
            />
          </div>
        </div>
      </transition-group>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useMainStore } from '@/stores/main'

// 🎯 Types
interface Notification {
  id: string
  type: 'info' | 'success' | 'warning' | 'error'
  title: string
  message: string
  category: string
  timestamp: Date
  read: boolean
  actions?: {
    label: string
    color?: string
    action: string
  }[]
}

interface ToastNotification extends Notification {
  autoHide?: boolean
}

// 🔧 Reactive state
const store = useMainStore()
const notificationType = ref('info')
const notificationPosition = ref('top-right')
const autoHide = ref(true)
const notifications = ref<Notification[]>([])
const toastNotifications = ref<ToastNotification[]>([])

const settings = ref({
  incidents: true,
  system: true,
  tasks: false,
  sound: true
})

// 📊 Mock data
const notificationTypes = ref([
  { label: 'Información', value: 'info' },
  { label: 'Éxito', value: 'success' },
  { label: 'Advertencia', value: 'warning' },
  { label: 'Error', value: 'error' }
])

const positionOptions = ref([
  { label: 'Arriba Derecha', value: 'top-right' },
  { label: 'Arriba Izquierda', value: 'top-left' },
  { label: 'Abajo Derecha', value: 'bottom-right' },
  { label: 'Abajo Izquierda', value: 'bottom-left' }
])

const mockNotifications = ref<Notification[]>([
  {
    id: '1',
    type: 'warning',
    title: 'Mantenimiento Programado',
    message: 'El sistema estará en mantenimiento el próximo domingo de 2:00 AM a 6:00 AM',
    category: 'Sistema',
    timestamp: new Date(Date.now() - 300000),
    read: false,
    actions: [
      { label: 'Ver detalles', action: 'view_details' },
      { label: 'Recordar', color: 'warning', action: 'remind' }
    ]
  },
  {
    id: '2',
    type: 'error',
    title: 'Incidencia Crítica Reportada',
    message: 'Se ha reportado una incidencia crítica en la Zona Norte que requiere atención inmediata',
    category: 'Incidencias',
    timestamp: new Date(Date.now() - 900000),
    read: false,
    actions: [
      { label: 'Atender', color: 'error', action: 'attend' },
      { label: 'Asignar', action: 'assign' }
    ]
  },
  {
    id: '3',
    type: 'success',
    title: 'Reporte Generado',
    message: 'El reporte semanal de recolección ha sido generado exitosamente',
    category: 'Reportes',
    timestamp: new Date(Date.now() - 1800000),
    read: true
  },
  {
    id: '4',
    type: 'info',
    title: 'Nueva Actualización Disponible',
    message: 'Hay una nueva versión del sistema disponible con mejoras de seguridad',
    category: 'Sistema',
    timestamp: new Date(Date.now() - 3600000),
    read: true,
    actions: [
      { label: 'Actualizar', color: 'primary', action: 'update' }
    ]
  }
])

// 🎯 Computed properties
const displayedNotifications = computed(() => {
  return [...notifications.value].sort((a, b) => 
    b.timestamp.getTime() - a.timestamp.getTime()
  )
})

const unreadCount = computed(() => 
  notifications.value.filter(n => !n.read).length
)

const notificationStats = computed(() => [
  {
    type: 'info',
    label: 'Información',
    count: notifications.value.filter(n => n.type === 'info').length
  },
  {
    type: 'success',
    label: 'Éxito',
    count: notifications.value.filter(n => n.type === 'success').length
  },
  {
    type: 'warning',
    label: 'Advertencias',
    count: notifications.value.filter(n => n.type === 'warning').length
  },
  {
    type: 'error',
    label: 'Errores',
    count: notifications.value.filter(n => n.type === 'error').length
  }
])

// 🎯 Methods
const getNotificationColor = (type: string) => {
  const colors: Record<string, string> = {
    info: 'info',
    success: 'success',
    warning: 'warning',
    error: 'error'
  }
  return colors[type] || 'grey'
}

const getNotificationIcon = (type: string) => {
  const icons: Record<string, string> = {
    info: 'mdi-information',
    success: 'mdi-check-circle',
    warning: 'mdi-alert',
    error: 'mdi-alert-circle'
  }
  return icons[type] || 'mdi-bell'
}

const createTestNotification = () => {
  const newNotification: Notification = {
    id: Date.now().toString(),
    type: notificationType.value as any,
    title: `Notificación de ${notificationType.value}`,
    message: `Esta es una notificación de prueba del tipo ${notificationType.value}`,
    category: 'Demo',
    timestamp: new Date(),
    read: false
  }

  notifications.value.unshift(newNotification)

  // Create toast notification
  const toastNotification: ToastNotification = {
    ...newNotification,
    autoHide: autoHide.value
  }
  
  toastNotifications.value.push(toastNotification)

  if (autoHide.value) {
    setTimeout(() => {
      dismissToast(toastNotification)
    }, 5000)
  }

  store.showNotification({
    type: notificationType.value as any,
    title: 'Notificación Creada',
    message: 'Se ha creado una nueva notificación de prueba'
  })
}

const markAsRead = (notification: Notification) => {
  notification.read = true
}

const markAllAsRead = () => {
  notifications.value.forEach(n => n.read = true)
  store.showNotification({
    type: 'success',
    title: 'Todas marcadas como leídas',
    message: 'Todas las notificaciones han sido marcadas como leídas'
  })
}

const dismissNotification = (notification: Notification) => {
  const index = notifications.value.findIndex(n => n.id === notification.id)
  if (index > -1) {
    notifications.value.splice(index, 1)
  }
}

const dismissToast = (toast: ToastNotification) => {
  const index = toastNotifications.value.findIndex(t => t.id === toast.id)
  if (index > -1) {
    toastNotifications.value.splice(index, 1)
  }
}

const clearAllNotifications = () => {
  notifications.value = []
  toastNotifications.value = []
  store.showNotification({
    type: 'info',
    title: 'Notificaciones Limpiadas',
    message: 'Todas las notificaciones han sido eliminadas'
  })
}

const executeAction = (notification: Notification, action: any) => {
  console.log('🔔 Ejecutando acción:', action.action, 'para notificación:', notification.id)
  store.showNotification({
    type: 'info',
    title: 'Acción Ejecutada',
    message: `Acción "${action.label}" ejecutada para: ${notification.title}`
  })
}

const formatTime = (date: Date) => {
  const now = new Date()
  const diff = now.getTime() - date.getTime()
  const minutes = Math.floor(diff / 60000)
  const hours = Math.floor(minutes / 60)
  const days = Math.floor(hours / 24)

  if (days > 0) return `hace ${days} día${days > 1 ? 's' : ''}`
  if (hours > 0) return `hace ${hours} hora${hours > 1 ? 's' : ''}`
  if (minutes > 0) return `hace ${minutes} minuto${minutes > 1 ? 's' : ''}`
  return 'hace un momento'
}

const showCode = () => {
  console.log('💻 Mostrar código del componente Notifications')
  store.showNotification({
    type: 'info',
    title: 'Código del Componente',
    message: 'Abriendo vista del código fuente de Notifications...'
  })
}

// 🎬 Lifecycle
onMounted(() => {
  console.log('🔔 Demo de Notifications cargado')
  notifications.value = [...mockNotifications.value]
})
</script>

<style scoped>
.notifications-demo {
  max-width: 1200px;
  margin: 0 auto;
  padding: 24px;
}

.notifications-list {
  max-height: 500px;
  overflow-y: auto;
}

.notification-item {
  padding: 16px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.notification-item:hover {
  background-color: rgba(0, 0, 0, 0.02);
}

.notification-unread {
  background-color: rgba(25, 118, 210, 0.05);
  border-left: 4px solid #1976d2;
}

.notification-info {
  border-left: 4px solid #2196f3;
}

.notification-success {
  border-left: 4px solid #4caf50;
}

.notification-warning {
  border-left: 4px solid #ff9800;
}

.notification-error {
  border-left: 4px solid #f44336;
}

.toast-container {
  position: fixed;
  z-index: 9999;
  pointer-events: none;
}

.toast-container > * {
  pointer-events: auto;
}

.toast-top-right {
  top: 20px;
  right: 20px;
}

.toast-top-left {
  top: 20px;
  left: 20px;
}

.toast-bottom-right {
  bottom: 20px;
  right: 20px;
}

.toast-bottom-left {
  bottom: 20px;
  left: 20px;
}

.toast-notification {
  min-width: 350px;
  max-width: 500px;
  margin-bottom: 8px;
  padding: 16px;
  border-radius: 8px;
  color: white;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  transition: all 0.3s ease;
}

.toast-notification:hover {
  transform: translateX(-4px);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.2);
}

.toast-info {
  background: linear-gradient(135deg, #2196f3, #1976d2);
}

.toast-success {
  background: linear-gradient(135deg, #4caf50, #388e3c);
}

.toast-warning {
  background: linear-gradient(135deg, #ff9800, #f57c00);
}

.toast-error {
  background: linear-gradient(135deg, #f44336, #d32f2f);
}

.toast-title {
  font-weight: 600;
  margin-bottom: 4px;
}

.toast-message {
  font-size: 14px;
  opacity: 0.9;
}

.toast-enter-active,
.toast-leave-active {
  transition: all 0.3s ease;
}

.toast-enter-from {
  opacity: 0;
  transform: translateX(100%);
}

.toast-leave-to {
  opacity: 0;
  transform: translateX(100%);
}

.bg-gradient-to-r {
  background: linear-gradient(90deg, #ff9800 0%, #f44336 100%);
}
</style>
